import { api } from './api';
import type { PreferencesDocument } from './types';
import type { SavedFilterStore, SavedFilterValues } from './use-saved-filters';

/**
 * The server-backed `SavedFilterStore` — the transport `useSavedFilters` was
 * written against, and the reason that hook takes its store by injection.
 *
 * Everything that DECIDES anything (a link wins entirely, saved filters are
 * written into the URL, what gets persisted and when) lives in
 * `use-saved-filters.ts` and is pure. This file only moves bytes.
 *
 * ---------------------------------------------------------------------------
 * ⚠️ THE ONE HARD PART: AN OBJECT BRANCH CANNOT BE REPLACED IN ONE REQUEST
 * ---------------------------------------------------------------------------
 *
 * `SavedFilterStore.save` is specified as a **whole-set REPLACE** — it is
 * handed the complete set for the scope, because *a cleared filter has to be
 * expressible*. The API cannot express that in one body:
 *
 *   | sent                        | happens                    |
 *   | --------------------------- | -------------------------- |
 *   | an object over an object    | merged **recursively**     |
 *   | `null`                      | that key is **DELETED**    |
 *   | a scalar or array           | replaces wholesale         |
 *
 * So `PATCH {tables: {deals: {stageId: '3'}}}` onto a stored
 * `{stageId: '1', currency: 'GBP'}` leaves **`currency` behind**. A person
 * clears a filter, the page looks right, and the filter comes back on their
 * next visit — a `200` the whole way, nothing to see in any log.
 *
 * ⚠️ **This is the `[] destroys / absent leaves alone` trap in its other
 * clothes**, and it fails in the direction where a cleared thing returns.
 *
 * The fix is to say what is GONE as well as what is set: every key in the
 * baseline that is absent from the new set is sent as an explicit `null`, in
 * the same body, and `null` deletes. The baseline is maintained from the
 * **responses**, never from what was sent, because the whole merged document
 * comes back on every write and that is the only place a key we did not expect
 * is visible.
 *
 * ⚠️ **Do NOT "simplify" this into sending the whole document.** Sending a
 * document read a moment ago is precisely the two-tab read-modify-write race
 * the server's row lock exists to remove, and it succeeds while losing the
 * other tab's keys. The narrow patch is the point.
 */

/** Where a page's saved filters live inside the document. */
const TABLES_KEY = 'tables';

/**
 * What we last saw stored, per scope — used ONLY to work out which keys have to
 * be deleted. It is never a cache of what to render: `load` always re-reads.
 *
 * Module-level rather than per-store-instance so that two pages mounted in one
 * session share it. `undefined` means "we have never seen this branch", which
 * is a genuinely different state from "we have seen it and it was empty" — see
 * `save`.
 */
const baselines = new Map<string, readonly string[]>();

function branchOf(document: PreferencesDocument, scope: string): unknown {
  const tables = document[TABLES_KEY];
  if (tables === null || typeof tables !== 'object' || Array.isArray(tables)) return null;
  return (tables as Record<string, unknown>)[scope] ?? null;
}

/**
 * The keys of a branch, as the server now holds them.
 *
 * Deliberately NOT filtered to the page's own key list: a key this build does
 * not recognise still occupies the branch, and forgetting it here would leave
 * it undeletable for ever. `load`'s result is separately sanitised against the
 * page's keys by the hook, which is the right place for that.
 */
function keysOf(branch: unknown): readonly string[] {
  if (branch === null || typeof branch !== 'object' || Array.isArray(branch)) return [];
  return Object.keys(branch as Record<string, unknown>);
}

export function createPreferencesStore(): SavedFilterStore {
  return {
    async load(scope, signal) {
      const response = await api.getPreferences(signal);
      const branch = branchOf(response.data.preferences, scope);
      baselines.set(scope, keysOf(branch));
      /*
       * Returned raw. The hook runs `sanitiseSavedValues` against the page's
       * own key list, which drops unknown keys, non-strings and blanks — so a
       * preferences document that changed shape degrades to "nothing saved"
       * rather than reaching a list. Filtering here as well would put a second
       * definition of "valid" in a second file, and the two would drift.
       */
      return (branch ?? null) as SavedFilterValues | null;
    },

    async save(scope, values) {
      const previous = baselines.get(scope);

      /*
       * The whole set is empty: delete the branch outright rather than writing
       * `{}`. This is the documented idiom for forgetting one page's filters
       * while leaving every other page alone, and it stops an empty object
       * accumulating in the document for every list anybody has ever cleared.
       */
      if (Object.keys(values).length === 0) {
        if (previous !== undefined && previous.length === 0) return;
        const response = await api.updatePreferences({
          preferences: { [TABLES_KEY]: { [scope]: null } },
        });
        baselines.set(scope, keysOf(branchOf(response.data.preferences, scope)));
        return;
      }

      const patch: Record<string, string | null> = { ...values };
      /*
       * ⚠️ The deletions, and the reason the baseline is tracked at all.
       *
       * When we have never seen the branch (`undefined`) there is nothing to
       * compute from, and this sends a plain merge — so a key we have never
       * read could survive a save that should have cleared it. That is stated
       * rather than engineered around because the hook loads on mount and only
       * takes its save baseline AFTER the arrival decision has been applied, so
       * a save without a preceding load is not reachable through it. If a
       * caller ever does reach it, the next `load` re-establishes the baseline
       * and the following save clears the straggler.
       */
      for (const key of previous ?? []) {
        if (!(key in patch)) patch[key] = null;
      }

      const response = await api.updatePreferences({
        preferences: { [TABLES_KEY]: { [scope]: patch } },
      });
      baselines.set(scope, keysOf(branchOf(response.data.preferences, scope)));
    },
  };
}

/**
 * One shared instance.
 *
 * `useSavedFilters` holds its store by ref, so a fresh object per render would
 * work — but a single instance is what lets the baselines above be shared, and
 * a store is stateless apart from them.
 */
export const preferencesStore = createPreferencesStore();

/**
 * Forget every baseline.
 *
 * ⚠️ **Call this on sign-out.** The baselines are keyed by scope and NOT by
 * user, because the API has no concept of another person's preferences — so
 * without this, signing out and signing in as somebody else on the same tab
 * would carry the first person's key list into the second person's first save,
 * and delete keys that were never theirs to lose. The support-session switch is
 * the realistic way to hit it.
 */
export function resetPreferenceBaselines(): void {
  baselines.clear();
}
